import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import { useFmt } from '@/lib/useFmt.js';
import {
  EmptyState,
  GlassButton,
  LoadingState,
  PageHeader,
  Pagination,
  Section,
  useConfirm,
} from '@/components/ui/index.js';
import type {
  PricingResponse,
  PromotionCodeListItem,
  PromotionCodesResponse,
} from '@/lib/types.js';

/**
 * Promotion codes — percent-off coupons attached to Stripe Checkout.
 * 不同于「兑换码（redeem_codes）」直接送会员，promo 是折扣码（结账时打折）。
 */

const PAGE_SIZE = 100;
const PAGE_OPENED_AT = Date.now();

export function PromotionCodesPage(): JSX.Element {
  const [data, setData] = useState<PromotionCodesResponse | null>(null);
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { fmtDate, fmtDateTime } = useFmt();
  const confirm = useConfirm();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (includeDeactivated) params.set('include_deactivated', '1');
      const res = await request<PromotionCodesResponse>(
        `/admin/promotion_codes?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [offset, includeDeactivated]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deactivate(p: PromotionCodeListItem): Promise<void> {
    const ok = await confirm({
      title: `失效 ${p.code}？`,
      message: (
        <>
          已经使用过该码的订单不受影响。新订单不能再使用此码。
          <br />
          <span className="text-slate-500 text-xs">同时会从 Stripe 删除对应 Coupon。</span>
        </>
      ),
      tone: 'danger',
      confirmLabel: '确认失效',
    });
    if (!ok) return;
    setBusyId(p.id);
    try {
      await request(`/admin/promotion_codes/${p.id}/deactivate`, { method: 'POST' });
      useToast.getState().show('success', `${p.code} 已失效`);
      await load();
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof ApiError ? err.message : '失效失败',
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="折扣码"
        subtitle="百分比折扣码。结账时用户自己输入，挂在 Stripe Coupon 上。"
        right={data !== null && <span className="text-sm text-slate-400">共 {data.total}</span>}
      />

      <CreateForm onCreated={(): void => void load()} />

      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeDeactivated}
            onChange={(e): void => { setIncludeDeactivated(e.target.checked); setOffset(0); }}
            className="accent-shark-500"
          />
          含已失效
        </label>
      </div>

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="mat-matte rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/[0.04]">
              <tr>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">码</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">折扣</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">已用 / 总量</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">有效期</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">状态</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">创建</th>
                <th className="px-4 py-3 text-left text-slate-300 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && data === null ? (
                <tr><td colSpan={7}><LoadingState /></td></tr>
              ) : data === null || data.codes.length === 0 ? (
                <tr><td colSpan={7}><EmptyState title="还没有 promotion code" hint="用上方表单创建第一个" icon="🎟️" /></td></tr>
              ) : (
                data.codes.map((p) => (
                  <PromoRow
                    key={p.id}
                    promo={p}
                    busy={busyId === p.id}
                    onDeactivate={(): void => void deactivate(p)}
                    fmtDate={fmtDate}
                    fmtDateTime={fmtDateTime}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        offset={offset}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

function CreateForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [code, setCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(20);
  const [maxUses, setMaxUses] = useState(20);
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [monthlyUsd, setMonthlyUsd] = useState<number | null>(null);
  const [yearlyUsd, setYearlyUsd] = useState<number | null>(null);

  // Pull live pricing once so the discount preview reflects whatever the
  // admin most recently set on /pricing — not a hardcoded stale value.
  useEffect(() => {
    let cancelled = false;
    void request<PricingResponse>('/admin/pricing')
      .then((res) => {
        if (cancelled) return;
        setMonthlyUsd(res.monthly?.usd ?? null);
        setYearlyUsd(res.yearly?.usd ?? null);
      })
      .catch(() => {
        // Preview not critical — silently fall back to hardcoded examples.
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (code === '') {
      useToast.getState().show('error', '请输入码');
      return;
    }
    setSubmitting(true);
    try {
      const normalizedCode = code.trim().toUpperCase();
      const body: Record<string, unknown> = {
        code: normalizedCode,
        discount_percent: discountPercent,
        max_uses: maxUses,
      };
      if (expiresAt !== '') {
        // Datetime-local → ISO string in local TZ
        body['expires_at'] = new Date(expiresAt).toISOString();
      }
      await request('/admin/promotion_codes', { method: 'POST', body });
      // toast 用规范化后的码, 与落库一致(原先用未 trim 的原始输入)(审计 L7)
      useToast.getState().show('success', `已创建 ${normalizedCode}`);
      setCode('');
      setExpiresAt('');
      onCreated();
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof ApiError ? err.message : '创建失败',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Section className="mb-6">
      <h2 className="text-sm font-semibold text-slate-300 mb-2">创建新 promotion code</h2>
      <p className="text-xs text-slate-500 mb-4">
        🎟️ 每个用户限用 1 次。创建后自动同步到 Stripe 后台。失效后已存在订单不受影响。
      </p>
      <form onSubmit={(event): void => { void submit(event); }} className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="promo-code" className="block text-xs text-slate-400 mb-1">码（自动转大写）</label>
          <input
            type="text"
            id="promo-code"
            value={code}
            onChange={(e): void => setCode(e.target.value.toUpperCase())}
            placeholder="WELCOME20"
            maxLength={32}
            className="w-40 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200 font-mono"
          />
        </div>
        <div>
          <label htmlFor="promo-discount" className="block text-xs text-slate-400 mb-1">
            <span className="text-shark-300 font-semibold">减百分之几</span>
            <span className="ml-1 text-slate-500">（1-99）</span>
          </label>
          <input
            type="number"
            min={1}
            max={99}
            id="promo-discount"
            value={discountPercent}
            onChange={(e): void => setDiscountPercent(Math.max(1, Math.min(99, Number(e.target.value))))}
            className="w-24 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
          />
          <DiscountPreview
            percent={discountPercent}
            monthlyUsd={monthlyUsd}
            yearlyUsd={yearlyUsd}
            onPick={setDiscountPercent}
          />
        </div>
        <div>
          <label htmlFor="promo-maxuses" className="block text-xs text-slate-400 mb-1">总用量上限</label>
          <input
            type="number"
            min={1}
            max={100000}
            id="promo-maxuses"
            value={maxUses}
            onChange={(e): void => setMaxUses(Math.max(1, Number(e.target.value)))}
            className="w-28 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
          />
        </div>
        <div>
          <label htmlFor="promo-expires" className="block text-xs text-slate-400 mb-1">有效期（选填）</label>
          <input
            type="datetime-local"
            id="promo-expires"
            value={expiresAt}
            onChange={(e): void => setExpiresAt(e.target.value)}
            className="px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
          />
        </div>
        <GlassButton type="submit" variant="primary" disabled={submitting}>
          {submitting ? '创建中…' : '创建'}
        </GlassButton>
      </form>
    </Section>
  );
}

function PromoRow({
  promo,
  busy,
  onDeactivate,
  fmtDate,
  fmtDateTime,
}: {
  promo: PromotionCodeListItem;
  busy: boolean;
  onDeactivate: () => void;
  fmtDate: (s: string | null | undefined) => string;
  fmtDateTime: (s: string | null | undefined) => string;
}): JSX.Element {
  const isDeactivated = promo.deactivated_at !== null;
  const isExpired =
    promo.expires_at !== null && new Date(promo.expires_at).getTime() < PAGE_OPENED_AT;
  const isSoldOut = promo.uses_count >= promo.max_uses;

  let statusBadge: { label: string; color: string };
  if (isDeactivated) statusBadge = { label: '已失效', color: 'text-rose-300' };
  else if (isExpired) statusBadge = { label: '已过期', color: 'text-amber-300' };
  else if (isSoldOut) statusBadge = { label: '已用完', color: 'text-amber-300' };
  else statusBadge = { label: '✓ 有效', color: 'text-emerald-300' };

  return (
    <tr className={`border-t border-white/[0.08] ${isDeactivated ? 'opacity-60' : ''}`}>
      <td className="px-4 py-3 font-mono text-slate-200 font-semibold">{promo.code}</td>
      <td className="px-4 py-3 text-shark-300">
        减 {promo.discount_percent}%
        <span className="text-xs text-slate-500 ml-1">
          （{(10 - promo.discount_percent / 10).toFixed(1)} 折）
        </span>
      </td>
      <td className="px-4 py-3 text-slate-300">
        <span className={isSoldOut ? 'text-amber-300' : ''}>
          {promo.uses_count}
        </span>
        <span className="text-slate-500"> / {promo.max_uses}</span>
      </td>
      <td className="px-4 py-3 text-slate-400 text-xs">
        {promo.expires_at !== null ? fmtDate(promo.expires_at) : '无限期'}
      </td>
      <td className={`px-4 py-3 text-xs ${statusBadge.color}`}>{statusBadge.label}</td>
      <td className="px-4 py-3 text-slate-400 text-xs">{fmtDateTime(promo.created_at)}</td>
      <td className="px-4 py-3">
        {!isDeactivated && (
          <GlassButton size="sm" variant="danger" disabled={busy} onClick={onDeactivate}>
            失效
          </GlassButton>
        )}
      </td>
    </tr>
  );
}

/**
 * Live preview under the discount-percent input. Shows three things:
 * - The 折 conversion ("减 20% = 打 8 折") so admins steeped in Chinese
 *   pricing conventions can sanity-check
 * - Actual checkout numbers for monthly + yearly so admins don't have to
 *   do the math (this is where YONO98=1%→$9.89 confusion came from)
 * - Quick-pick chips for the most useful percents — 20 / 50 / 99 cover
 *   "promo", "half off", "tester near-free" without typing
 */
function DiscountPreview({
  percent,
  monthlyUsd,
  yearlyUsd,
  onPick,
}: {
  percent: number;
  monthlyUsd: number | null;
  yearlyUsd: number | null;
  onPick?: (n: number) => void;
}): JSX.Element {
  const folded = (10 - percent / 10).toFixed(1);
  // Discount preview is informational; null usd means pricing API errored —
  // we still render the 折 conversion as the minimum useful feedback.
  const monthlyAfter = monthlyUsd !== null
    ? (monthlyUsd * (1 - percent / 100)).toFixed(2)
    : null;
  const yearlyAfter = yearlyUsd !== null
    ? (yearlyUsd * (1 - percent / 100)).toFixed(2)
    : null;

  // Highlight a "tester" hint when percent is at 99 (or above 90) so the
  // admin knows they're in test-coupon territory, not real promo.
  const isTesterRange = percent >= 90;

  return (
    <div className="mt-1.5 space-y-1">
      <p className={`text-[11px] ${isTesterRange ? 'text-amber-300' : 'text-slate-400'}`}>
        减 <span className="font-semibold">{percent}%</span>
        <span className="text-slate-500 mx-1">·</span>
        打 <span className="font-semibold">{folded}</span> 折
        {isTesterRange && <span className="ml-2">🧪 测试码档</span>}
      </p>
      {(monthlyAfter !== null || yearlyAfter !== null) && (
        <p className="text-[10px] text-slate-500 leading-relaxed">
          {monthlyAfter !== null && (
            <>
              月卡：<span className="text-slate-400">${monthlyUsd?.toFixed(2)}</span>
              <span className="text-slate-600 mx-1">→</span>
              <span className="text-shark-300 font-semibold">${monthlyAfter}</span>
            </>
          )}
          {monthlyAfter !== null && yearlyAfter !== null && (
            <span className="mx-2 text-slate-700">·</span>
          )}
          {yearlyAfter !== null && (
            <>
              年卡：<span className="text-slate-400">${yearlyUsd?.toFixed(2)}</span>
              <span className="text-slate-600 mx-1">→</span>
              <span className="text-shark-300 font-semibold">${yearlyAfter}</span>
            </>
          )}
        </p>
      )}
      {onPick !== undefined && (
        <div className="flex gap-1 mt-1">
          {[20, 50, 99].map((p) => (
            <button
              key={p}
              type="button"
              onClick={(): void => onPick(p)}
              className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                percent === p
                  ? 'bg-shark-500/20 border-shark-500/50 text-shark-200'
                  : 'bg-white/[0.03] border-white/10 text-slate-400 hover:border-white/10 hover:text-slate-200'
              }`}
            >
              {p === 99 ? '99 (测试)' : p === 50 ? '50 (半价)' : `${p} (常规)`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
